import { properties as propertiesApi } from '../api';
import { createPageHeader } from '../components/form';
import { createDataTable, type Column } from '../components/table';
import { createStatCard } from '../components/card';
import { navigate } from '../router';
import type { Property } from '../types';

function daysUntil(dateStr: string): number {
  if (!dateStr) return Infinity;
  const d = new Date(dateStr);
  const now = new Date();
  return Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

function statusBadge(dateStr: string): string {
  if (!dateStr) return '<span class="badge badge-neutral">Not Set</span>';
  const days = daysUntil(dateStr);
  if (days < 0) return `<span class="badge badge-danger">Expired</span>`;
  if (days <= 30) return `<span class="badge badge-warning">${days}d</span>`;
  if (days <= 90) return `<span class="badge badge-info">${days}d</span>`;
  return `<span class="badge badge-success">OK</span>`;
}

interface ComplianceRow {
  property: Property;
  type: 'Gas Safety' | 'Electrical Safety' | 'Smoke Alarms';
  provider: string;
  expiryDate: string;
  daysLeft: number;
}

export async function compliancePage(): Promise<HTMLElement> {
  const page = document.createElement('div');
  page.appendChild(createPageHeader('Compliance'));

  let propList: Property[] = [];
  try {
    propList = await propertiesApi.list();
  } catch {
    page.innerHTML += '<div class="text-red-500 p-4">Failed to load properties.</div>';
    return page;
  }

  // Build rows: one row per property per compliance type
  const rows: ComplianceRow[] = [];
  for (const p of propList) {
    rows.push({
      property: p,
      type: 'Gas Safety',
      provider: p.gas_safety_provider || '',
      expiryDate: p.gas_safety_expiry || '',
      daysLeft: daysUntil(p.gas_safety_expiry),
    });
    rows.push({
      property: p,
      type: 'Electrical Safety',
      provider: p.electrical_safety_provider || '',
      expiryDate: p.electrical_safety_expiry || '',
      daysLeft: daysUntil(p.electrical_safety_expiry),
    });
    rows.push({
      property: p,
      type: 'Smoke Alarms',
      provider: p.smoke_alarm_provider || '',
      expiryDate: p.smoke_alarm_expiry || '',
      daysLeft: daysUntil(p.smoke_alarm_expiry),
    });
  }

  // Summary stats
  const expired = rows.filter(r => r.daysLeft < 0 && r.expiryDate);
  const expiringSoon = rows.filter(r => r.daysLeft >= 0 && r.daysLeft <= 60);
  const notSet = rows.filter(r => !r.expiryDate);
  const compliant = rows.filter(r => r.daysLeft > 60);

  const statsRow = document.createElement('div');
  statsRow.className = 'grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6';
  statsRow.appendChild(createStatCard('Compliant', `${compliant.length}`, `of ${rows.length} checks`, 'text-green-600'));
  statsRow.appendChild(createStatCard('Expiring Soon', `${expiringSoon.length}`, '≤ 60 days', expiringSoon.length > 0 ? 'text-amber-600' : undefined));
  statsRow.appendChild(createStatCard('Expired', `${expired.length}`, undefined, expired.length > 0 ? 'text-red-600' : undefined));
  statsRow.appendChild(createStatCard('Not Set', `${notSet.length}`, undefined, notSet.length > 0 ? 'text-gray-500' : undefined));
  page.appendChild(statsRow);

  // Filter controls
  const filters = document.createElement('div');
  filters.className = 'flex gap-4 mb-4 flex-wrap';
  filters.innerHTML = `
    <select id="comp-type-filter" class="input-field w-auto py-1 text-sm">
      <option value="">All Types</option>
      <option value="Gas Safety">Gas Safety</option>
      <option value="Electrical Safety">Electrical Safety</option>
      <option value="Smoke Alarms">Smoke Alarms</option>
    </select>
    <select id="comp-status-filter" class="input-field w-auto py-1 text-sm">
      <option value="">All Status</option>
      <option value="expired">Expired</option>
      <option value="expiring">Expiring Soon (≤60d)</option>
      <option value="ok">Compliant</option>
      <option value="notset">Not Set</option>
    </select>
  `;
  page.appendChild(filters);

  const tableContainer = document.createElement('div');
  page.appendChild(tableContainer);

  function renderFiltered() {
    const typeVal = (page.querySelector('#comp-type-filter') as HTMLSelectElement).value;
    const statusVal = (page.querySelector('#comp-status-filter') as HTMLSelectElement).value;

    let filtered = [...rows];
    if (typeVal) filtered = filtered.filter(r => r.type === typeVal);
    if (statusVal === 'expired') filtered = filtered.filter(r => r.daysLeft < 0 && r.expiryDate);
    else if (statusVal === 'expiring') filtered = filtered.filter(r => r.daysLeft >= 0 && r.daysLeft <= 60);
    else if (statusVal === 'ok') filtered = filtered.filter(r => r.daysLeft > 60);
    else if (statusVal === 'notset') filtered = filtered.filter(r => !r.expiryDate);

    // Sort: expired first, then by days left ascending
    filtered.sort((a, b) => {
      if (a.daysLeft === Infinity && b.daysLeft === Infinity) return 0;
      if (a.daysLeft === Infinity) return 1;
      if (b.daysLeft === Infinity) return -1;
      return a.daysLeft - b.daysLeft;
    });

    const cols: Column<ComplianceRow>[] = [
      {
        label: 'Property',
        render: r => `<a href="#/properties/${r.property.id}" class="text-primary-600 hover:underline font-medium">${r.property.address}, ${r.property.suburb}</a>`,
      },
      { label: 'Type', render: r => r.type },
      {
        label: 'Provider',
        render: r => r.provider || '<span class="text-gray-400">—</span>',
      },
      {
        label: 'Expiry Date',
        render: r => r.expiryDate
          ? `${r.expiryDate} ${statusBadge(r.expiryDate)}`
          : '<span class="text-gray-400">Not set</span>',
      },
      {
        label: '',
        render: r => `<button class="btn-ghost text-sm edit-btn" data-id="${r.property.id}">Edit</button>`,
      },
    ];

    tableContainer.innerHTML = '';
    const table = createDataTable(cols, filtered, { emptyText: 'No compliance records match the filter.' });
    tableContainer.appendChild(table);

    table.querySelectorAll('.edit-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-id')!;
        navigate(`#/properties/${id}/edit`);
      });
    });
  }

  filters.querySelectorAll('select').forEach(sel => {
    sel.addEventListener('change', renderFiltered);
  });

  renderFiltered();
  return page;
}
